import { Inject, Injectable } from "@webmotors/ioc";
import { WEBSOCKET } from "./websocket.types";
import { CONFIG, type Config } from "@packages/config";
import { LOGGER, type Logger } from "../logger";;

@Injectable(WEBSOCKET)
export class WebSocketService {
  private ws?: WebSocket;
  private listeners: Map<string, (data: any) => void> = new Map();
  private readonly logger: Logger;

  /**
   * WebSocket Service constructor
   *
   * @param logger
   * @param config
   */
  constructor(
    @Inject(LOGGER) private readonly loggerFactory: Logger,
    @Inject(CONFIG) private readonly config: Config,
  ) {
    this.logger = this.loggerFactory.create(WEBSOCKET);
  }

  /** 
   * Method to connect with de WS
   * 
   * @param token (user jwt token)
   */
  connect(path: string): void {
    this.ws = new WebSocket(`${this.config.chatApiUrl}${path}`);

    this.ws.onopen = () => {
      this.handleMessage('WebSocket connection open.');
      this.listeners.get("open")?.({});
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const action = data.action;
        if (action && this.listeners.has(action)) {
          this.listeners.get(action)?.(data);
        }
      } catch (error) {
        this.handleMessage("Error parsing WebSocket message", error);
      }
    };

    this.ws.onerror = (error) => {
      this.handleMessage("Error connecting to WebSocket", error);
    };

    this.ws.onclose = (event) => {
      this.handleMessage(`WebSocket closed with code: ${event.code}, event: ${event}`);
    };
  }

  /** 
   * Method to apply a callback according to WS message status
   * 
   * @param action 
   * @param callback 
   */
  on(action: string, callback: (data: any) => void): void {
    this.listeners.set(action, callback);
  }

  /** 
   * Method to comunicate with de WS
   * 
   * @param action 
   * @param payload 
   */
  send(action: string, payload: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.handleMessage("Error connecting to WebSocket");
      throw new Error("WebSocket connection is not open.");
    }
    this.ws.send(JSON.stringify({ action, ...payload }));
  }



  /**
   * Method to close WS connection
   */
  close(): void {
    if (this.ws) {
      this.ws.close();
      this.listeners.clear();
      this.ws = undefined;
    }
  }

  /**
   * Helper to log messages
   * 
   * @param message 
   * @param error 
   */
  private handleMessage(message: string, error?: unknown): void {
    if (error && error instanceof Error) {
      this.logger.error(message, error.message);
    } else {
      this.logger.log(message);
    }
  }
}
